// src/design/DesignCenterAIAssist.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getStrings, getSectionOpening } from './designCenterLocale';

const API_URL = process.env.REACT_APP_API_URL;
const THINK_DELAY = 3000; // ms before each scripted AI message appears

// Section opening prompts and stock UI strings now live in
// designCenterLocale.js, indexed by language (en/es/ja/th). The user's
// preferred language is fetched from their profile on mount and applied to
// every assistant-generated string. UI chrome (buttons, etc.) stays English.

/**
 * Typing indicator — animated dots
 */
function TypingIndicator() {
  return (
    <div className="mr-auto bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-1">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * AI Assist chat — collects a brief through section-specific questions, then calls
 * the real OpenAI endpoint to generate a headline + platform captions.
 *
 * REVISION STRATEGY:
 * - ONE endpoint: /api/designcenter/ai/generate-captions
 * - First call sends { brief, category } → full generation
 * - Every subsequent call sends { existing, revision } → cheap revision
 * - cachedResultRef stores the last result and is NEVER cleared.
 */
export default function DesignCenterAIAssist({
  selected,
  activeTabLabel,
  sectionKey,
  onCaptionsGenerated,
  onBriefComplete,
  // The headline the user has already chosen (from an inspiration
  // card or typed by hand). If non-empty, we pass it to the AI so it
  // preserves the user's wording instead of generating a new one.
  // The user explicitly chose that title — replacing it on caption
  // generation felt like the AI overriding their decision.
  existingHeadline,
  // headlineIsUserTyped (default false) — when true, existingHeadline is sent
  // to the backend and triggers the 'HEADLINE IS LOCKED, return verbatim' rule.
  // Now true for BOTH user-typed AND inspiration-chosen headlines: per Brett
  // (2026-06-07) the inspiration title must remain the headline verbatim unless
  // the user edits it. No language-mixing risk: inspiration cards are gated per
  // language (designcenter_inspiration is keyed by language), so a Thai user
  // only ever sees Thai cards, Spanish→Spanish, Japanese→Japanese — the locked
  // headline is already in the user's language. (Supersedes the 2026-05-25
  // Lekkey auto-translate concern; the per-language pools handle it.)
  headlineIsUserTyped = false,
  inspirationAnswer,
  inspirationDismissed,
  inspirationLoading,
  inspirationIdeas,
  onReopenInspiration,
}) {
  // User's preferred language for assistant strings (en/es/ja/th). Fetched
  // once on mount from /api/users/profile. Defaults to 'en' until the fetch
  // resolves — the worst case is the opening prompt flashes English for a
  // few hundred ms before settling, which is acceptable for the simplicity
  // benefit vs hydrating into a Zustand store.
  const [userLang, setUserLang] = useState('en');
  const strings = useMemo(() => getStrings(userLang), [userLang]);

  // stringsRef tracks the LATEST strings so async event handlers
  // (showResultsInChat, callAI, sendDelayedAI, etc.) read the right
  // localized text at execution time — not at the time the callback
  // was created. Without this, an in-flight AI call started before the
  // userLang fetch resolves would push English UI strings even though
  // userLang ended up resolving to 'th'. Bug surfaced 2026-05-25 with
  // Lekkey's first Thai post (intro strings rendered in English while
  // captions came back in Thai). Render-time references like the input
  // placeholder still use `strings` directly because they always re-read
  // on each render.
  const stringsRef = useRef(strings);
  useEffect(() => { stringsRef.current = strings; }, [strings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/users/profile`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const lang = data?.u_preferred_language;
        if (lang && ['en', 'es', 'ja', 'th'].includes(lang)) {
          setUserLang(lang);
        }
      } catch {
        // Silent — English fallback is fine
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Conversation state — reset when section changes
  const [messages, setMessages] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [brief, setBrief] = useState({});
  const cachedResultRef = useRef(null);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const endRef = useRef(null);
  const thinkTimerRef = useRef(null);
  const prevSectionRef = useRef(sectionKey);
  const inputRef = useRef(null);

  // Reset conversation when section changes
  useEffect(() => {
    if (sectionKey !== prevSectionRef.current) {
      prevSectionRef.current = sectionKey;
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: getSectionOpening(userLang, sectionKey),
      }]);
      setQuestionIndex(0);
      setBrief({});
      cachedResultRef.current = null;
      setInput('');
      setIsThinking(false);
      initialSigRef.current = null;
    }
  }, [sectionKey, userLang]);

  // Initialize on first mount (and re-render the opening if userLang
  // resolves after mount)
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: getSectionOpening(userLang, sectionKey),
      }]);
    } else if (messages.length === 1 && messages[0].role === 'ai') {
      // userLang resolved after mount and we've only shown the opening —
      // swap it for the localized version so the first impression matches.
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: getSectionOpening(userLang, sectionKey),
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLang]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
    };
  }, []);

  // Keep a stable "selected signature"
  const selectedSig = useMemo(() => {
    if (!selected?.filename) return '';
    return `${selected.filename}`;
  }, [selected?.filename]);

  // Track whether user has made at least one image selection (skip initial auto-select)
  const initialSigRef = useRef(null);

  // When the selected image changes, acknowledge it (but not on first load)
  useEffect(() => {
    if (!selectedSig) return;

    // First image selected = initial auto-select, just record it
    if (initialSigRef.current === null) {
      initialSigRef.current = selectedSig;
      return;
    }

    // Same image as initial = no-op
    if (selectedSig === initialSigRef.current && !cachedResultRef.current) return;

    setMessages((prev) => {
      const notice = stringsRef.current.image_updated;
      const last = prev[prev.length - 1];

      if (last?.role === 'ai' && last?.meta === 'image-change') {
        return [
          ...prev.slice(0, -1),
          { ...last, id: crypto.randomUUID(), text: notice, sig: selectedSig },
        ];
      }

      return [
        ...prev,
        { id: crypto.randomUUID(), role: 'ai', text: notice, meta: 'image-change', sig: selectedSig },
      ];
    });
  }, [selectedSig]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isThinking]);

  // Auto-focus input when AI finishes thinking
  useEffect(() => {
    if (!isThinking) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isThinking]);

  // Fetch AI-generated follow-up question based on user's first answer
  const fetchFollowUp = useCallback(async (userAnswer) => {
    try {
      const res = await fetch(`${API_URL}/api/designcenter/ai/follow-up`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: activeTabLabel || 'general', userAnswer }),
      });
      const data = await res.json();
      return data.ok ? data.followUp : null;
    } catch {
      return null;
    }
  }, [activeTabLabel]);

  // Auto-answer first question when an inspiration idea was chosen
  const inspirationUsedRef = useRef(false);
  useEffect(() => {
    if (!inspirationAnswer || inspirationUsedRef.current) return;
    if (questionIndex !== 0 || messages.length < 1) return;
    const timer = setTimeout(() => {
      if (inspirationUsedRef.current) return;
      inspirationUsedRef.current = true;

      // Show the answer as a user message
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text: inspirationAnswer }]);
      setBrief((b) => ({ ...b, goal: inspirationAnswer }));
      setQuestionIndex(1);

      // Fetch AI follow-up
      setIsThinking(true);
      (async () => {
        const followUp = await fetchFollowUp(inspirationAnswer);
        setIsThinking(false);
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'ai',
          text: followUp || stringsRef.current.follow_up_fallback,
        }]);
      })();
    }, 500);
    return () => clearTimeout(timer);
  }, [inspirationAnswer, questionIndex, messages.length, fetchFollowUp]);

  const pushMessage = useCallback((role, text, extra) => {
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role, text, ...extra }]);
  }, []);

  // Delayed scripted AI response
  const sendDelayedAI = useCallback((acknowledgment, followUp) => {
    setIsThinking(true);

    thinkTimerRef.current = setTimeout(() => {
      pushMessage('ai', acknowledgment);
      setIsThinking(false);

      if (followUp) {
        setTimeout(() => {
          setIsThinking(true);
          thinkTimerRef.current = setTimeout(() => {
            pushMessage('ai', followUp);
            setIsThinking(false);
          }, THINK_DELAY);
        }, 200);
      }
    }, THINK_DELAY);
  }, [pushMessage]);

  // Show generated results in chat
  const showResultsInChat = useCallback((data, isRevision) => {
    const s = stringsRef.current;
    pushMessage('ai', isRevision ? s.results_updated_intro : s.results_intro);

    setTimeout(() => {
      pushMessage('ai', `"${data.headline}"`, { meta: 'headline' });
    }, 300);

    setTimeout(() => {
      const captionLines = [];
      if (data.captions?.facebook) captionLines.push(`Facebook: ${data.captions.facebook}`);
      if (data.captions?.instagram) captionLines.push(`Instagram: ${data.captions.instagram}`);
      if (data.captions?.linkedin) captionLines.push(`LinkedIn: ${data.captions.linkedin}`);
      if (data.captions?.youtube) captionLines.push(`YouTube: ${data.captions.youtube}`);
      if (data.captions?.twitter) captionLines.push(`Twitter/X: ${data.captions.twitter}`);
      if (data.captions?.tiktok) captionLines.push(`TikTok: ${data.captions.tiktok}`);
      if (data.captions?.whatsapp) captionLines.push(`WhatsApp: ${data.captions.whatsapp}`);

      pushMessage('ai', captionLines.join('\n\n'), { meta: 'captions' });
    }, 600);

    setTimeout(() => {
      pushMessage('ai', stringsRef.current.results_outro);
      setIsThinking(false);
    }, 900);
  }, [pushMessage]);

  /**
   * callAI — ONE function for ALL AI calls.
   */
  const callAI = useCallback(async (payload) => {
    setIsThinking(true);

    try {
      const res = await fetch(`${API_URL}/api/designcenter/ai/generate-captions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        pushMessage('ai', stringsRef.current.error_generic(data.error));
        setIsThinking(false);
        return;
      }

      const isRevision = !!payload.existing;

      // Update cache — always
      cachedResultRef.current = { headline: data.headline, captions: data.captions };

      // Pass up to parent
      if (onCaptionsGenerated) {
        onCaptionsGenerated({ headline: data.headline, captions: data.captions });
      }

      showResultsInChat(data, isRevision);

    } catch (err) {
      console.error('[DesignCenterAIAssist] callAI error:', err);
      pushMessage('ai', stringsRef.current.error_network);
      setIsThinking(false);
    }
  }, [onCaptionsGenerated, pushMessage, showResultsInChat]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isThinking) return;

    pushMessage('user', text);
    setInput('');

    // ━━━ REVISION — if we have cached data, ALWAYS use cheap revision ━━━
    if (cachedResultRef.current) {
      pushMessage('ai', stringsRef.current.updating);
      callAI({
        existing: cachedResultRef.current,
        revision: text,
      });
      return;
    }

    // ━━━ Step 1: User answered the opening question ━━━
    if (questionIndex === 0) {
      setBrief((b) => ({ ...b, goal: text }));
      setQuestionIndex(1);

      // Call AI for a natural follow-up question
      setIsThinking(true);
      (async () => {
        const followUp = await fetchFollowUp(text);
        setIsThinking(false);
        if (followUp) {
          pushMessage('ai', followUp);
        } else {
          // Fallback if AI call fails
          pushMessage('ai', stringsRef.current.follow_up_fallback);
        }
      })();
      return;
    }

    // ━━━ Step 2: User answered the follow-up — generate captions ━━━
    if (questionIndex === 1) {
      const completedBrief = { ...brief, goal: brief.goal, context: text };
      setBrief(completedBrief);
      setQuestionIndex(2);

      if (onBriefComplete) onBriefComplete(completedBrief);

      pushMessage('ai', stringsRef.current.generating);
      // If the user already has a headline (picked from an inspiration
      // card or typed by hand), pass it through so the AI preserves it
      // instead of generating a competing one.
      callAI({
        brief: completedBrief,
        category: activeTabLabel || 'general',
        // LOCK the headline (preserve verbatim) when it's user-typed OR chosen
        // from an inspiration card (headlineIsUserTyped covers both now) — the
        // chosen title must survive verbatim instead of being rewritten.
        existingHeadline: headlineIsUserTyped
          ? ((existingHeadline || '').trim() || undefined)
          : undefined,
      });
      return;
    }

    // Fallback
    pushMessage('ai', stringsRef.current.adjust_or_preview);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
        {/* Chat window */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col flex-1 min-h-0">
        {/* Messages (scrolls) */}
        <div className="flex-1 min-h-0 overflow-auto overscroll-contain p-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {/* Reopen inspiration strip — only shown after the user has
                dismissed the strip. Previously a tiny blue-text button with
                a yellow hover, easy to miss. Now styled as a prominent
                brand-blue card with a pulsing lightbulb so users who closed
                the strip can find their way back instead of giving up. */}
            {inspirationDismissed && (inspirationLoading || (inspirationIdeas && inspirationIdeas.length > 0)) && (
              <button
                type="button"
                onClick={onReopenInspiration}
                className="eh-design-nodrag flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#00aef7] to-[#0098d8] hover:from-[#0098d8] hover:to-[#0086c0] text-white shadow-md hover:shadow-lg transition-all"
                title="Show inspiration ideas"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill="#FFD700" className="shrink-0 animate-pulse drop-shadow">
                  <path d="M423.5-103.5Q400-127 400-160h160q0 33-23.5 56.5T480-80q-33 0-56.5-23.5ZM320-200v-80h320v80H320Zm10-120q-69-41-109.5-110T180-580q0-125 87.5-212.5T480-880q125 0 212.5 87.5T780-580q0 81-40.5 150T630-320H330Zm24-80h252q45-32 69.5-79T700-580q0-92-64-156t-156-64q-92 0-156 64t-64 156q0 54 24.5 101t69.5 79Zm126 0Z" />
                </svg>
                <span className="text-sm font-bold">{strings.inspiration.need_inspiration}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            {messages.map((m) => (
            <div
                key={m.id}
                className={`max-w-[92%] rounded-lg px-2 py-1 text-[11px] leading-snug whitespace-pre-wrap break-words ${
                m.role === 'user'
                    ? 'ml-auto bg-[#00aef7] text-white'
                    : m.meta === 'headline'
                    ? 'mr-auto bg-yellow-50 border border-yellow-200 text-gray-900 font-semibold'
                    : m.meta === 'captions'
                    ? 'mr-auto bg-blue-50 border border-blue-200 text-gray-700'
                    : 'mr-auto bg-gray-100 text-gray-700'
                }`}
            >
                {m.text}
            </div>
            ))}
            {isThinking && <TypingIndicator />}
            <div ref={endRef} />
        </div>

        {/* Input (always pinned) */}
        <div className="shrink-0 border-t border-gray-200 p-2 flex gap-1 bg-white">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend();
            }}
            placeholder={isThinking ? '' : strings.input_placeholder}
            disabled={isThinking}
            className="eh-design-nodrag flex-1 min-w-0 px-2 py-1 rounded border border-gray-200 text-xs focus:outline-none focus:border-[#00aef7] disabled:bg-gray-50 disabled:text-gray-400"
          />

            <button
            type="button"
            onClick={handleSend}
            disabled={isThinking}
            className="eh-design-nodrag shrink-0 w-6 h-6 rounded-full bg-[#00aef7] hover:bg-[#0097d6] disabled:bg-gray-300 flex items-center justify-center"
            aria-label="Send"
            title="Send"
            >
            <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
            </svg>
            </button>

        </div>
      </div>
    </div>
  );
}
